// Head-pose (pitch / yaw / roll) from MediaPipe FaceLandmarker's facial transformation
// matrix. detectForVideo() with `outputFacialTransformationMatrixes: true` returns, per
// face, a 4x4 matrix that maps the canonical face model into camera space. Its top-left
// 3x3 block is the head's rotation; we extract Euler angles from it.
//
// This is kept as a pure, side-effect-free module so the loop in face-capture.tsx can
// call it on every detected frame, and so the math can be unit-tested in isolation.

export interface HeadPose {
  // Nodding up/down — rotation about the X axis (degrees).
  pitch: number;
  // Shaking left/right — rotation about the Y axis (degrees).
  yaw: number;
  // Tilting the head toward a shoulder — rotation about the Z axis (degrees).
  roll: number;
}

const RAD_TO_DEG = 180 / Math.PI;

// MediaPipe fills the matrix in COLUMN-MAJOR order, so the element at (row, col) is
// data[col * 4 + row]. The rotation submatrix is the top-left 3x3.
//
// We use the aerospace (Z-Y-X) Tait-Bryan extraction, which is numerically stable near
// the poles thanks to the hypot() term:
//   pitch (X) = atan2( r21, r22 )
//   yaw   (Y) = atan2(-r20, hypot(r21, r22) )
//   roll  (Z) = atan2( r10, r00 )
//
// A face looking straight at the camera yields ~identity rotation -> pitch/yaw/roll ~0.
// Sign convention follows the camera frame; the live preview is mirrored, so a consumer
// comparing against what the user SEES may need to negate `yaw`.
export function headPoseFromMatrix(data: number[] | null | undefined): HeadPose | null {
  if (!data || data.length < 16) return null;

  const el = (row: number, col: number) => data[col * 4 + row];
  const r00 = el(0, 0);
  const r10 = el(1, 0);
  const r20 = el(2, 0);
  const r21 = el(2, 1);
  const r22 = el(2, 2);

  const pitch = Math.atan2(r21, r22) * RAD_TO_DEG;
  const yaw = Math.atan2(-r20, Math.hypot(r21, r22)) * RAD_TO_DEG;
  const roll = Math.atan2(r10, r00) * RAD_TO_DEG;

  return { pitch, yaw, roll };
}

export const CENTER_POINT_REGISTER = 5;
export const CENTER_POINT_CHECKIN = 25;

export function isCentered(pose: HeadPose | null | undefined, centerPoint: number): boolean {
  if (!pose) return false;
  const pitch = Math.abs(Math.round(pose.pitch));
  const yaw = Math.abs(Math.round(pose.yaw));
  return pitch <= centerPoint && yaw <= centerPoint;
}
